import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ReportsTab = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [reportData, setReportData] = useState({
    totalQuotes: 0,
    wonQuotes: 0,
    totalRevenue: 0,
    averageQuoteValue: 0,
    conversionRate: 0,
    topProjectTypes: [],
    recentActivities: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadReportData();
    }
  }, [user, period]);

  const loadReportData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on period
      const now = new Date();
      let startDate;

      switch (period) {
        case 'week':
          startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now?.getFullYear(), now?.getMonth(), 1);
          break;
        case 'quarter':
          startDate = new Date(now?.getFullYear(), Math.floor(now?.getMonth() / 3) * 3, 1);
          break;
        case 'year':
          startDate = new Date(now?.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now?.getFullYear(), now?.getMonth(), 1);
      }

      // Get quotes data
      const { data: quotes, error: quotesError } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id)?.gte('quote_date', startDate?.toISOString()?.split('T')?.[0]);

      if (quotesError) throw quotesError;

      // Get sales data
      const { data: sales, error: salesError } = await supabase?.from('provider_sales')?.select('*')?.eq('provider_id', user?.id)?.gte('created_at', startDate?.toISOString());

      if (salesError) throw salesError;

      // Get activities
      const { data: activities, error: activitiesError } = await supabase?.from('crm_activities')?.select('*')?.eq('provider_id', user?.id)?.gte('activity_date', startDate?.toISOString())?.order('activity_date', { ascending: false })?.limit(20);

      if (activitiesError) throw activitiesError;

      // Calculate metrics
      const totalQuotes = quotes?.length || 0;
      const wonQuotes = quotes?.filter((q) => q?.status === 'gewonnen')?.length || 0;
      const totalRevenue = sales?.reduce((sum, s) => sum + (parseFloat(s?.sale_amount) || 0), 0) || 0;
      const totalQuoteValue = quotes?.reduce((sum, q) => sum + (parseFloat(q?.quote_amount) || 0), 0) || 0;
      const averageQuoteValue = totalQuotes > 0 ? totalQuoteValue / totalQuotes : 0;
      const conversionRate = totalQuotes > 0 ? (wonQuotes / totalQuotes) * 100 : 0;

      // Calculate top project types
      const projectTypeMap = {};
      quotes?.forEach((quote) => {
        const type = quote?.project_type;
        if (!projectTypeMap?.[type]) {
          projectTypeMap[type] = { count: 0, revenue: 0 };
        }
        projectTypeMap[type].count++;
        if (quote?.status === 'gewonnen') {
          projectTypeMap[type].revenue += parseFloat(quote?.quote_amount) || 0;
        }
      });

      const topProjectTypes = Object.entries(projectTypeMap)
        ?.map(([type, data]) => ({
          type,
          count: data?.count,
          revenue: data?.revenue,
        }))
        ?.sort((a, b) => b?.revenue - a?.revenue)
        ?.slice(0, 5);

      setReportData({
        totalQuotes,
        wonQuotes,
        totalRevenue,
        averageQuoteValue,
        conversionRate,
        topProjectTypes,
        recentActivities: activities || [],
      });
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Get all quotes for export
      const { data: quotes, error } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id)?.order('quote_date', { ascending: false });

      if (error) throw error;

      // Create CSV content
      const headers = [
        'Offertenummer',
        'Klantnaam',
        'Email',
        'Telefoon',
        'Type',
        'Bedrag',
        'Status',
        'Datum',
        'Geldig Tot',
      ];

      const rows = quotes?.map((q) => [
        q?.quote_number,
        q?.customer_name,
        q?.customer_email,
        q?.customer_phone || '',
        q?.project_type,
        q?.quote_amount,
        q?.status,
        q?.quote_date,
        q?.valid_until || '',
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map((row) => row?.map((cell) => `"${cell}"`)?.join(',')),
      ]?.join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `crm-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const periodOptions = [
    { value: 'week', label: 'Deze Week' },
    { value: 'month', label: 'Deze Maand' },
    { value: 'quarter', label: 'Dit Kwartaal' },
    { value: 'year', label: 'Dit Jaar' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Rapportage</h2>
          <p className="text-muted-foreground mt-1">Overzicht van prestaties en activiteiten</p>
        </div>
        <Button onClick={handleExportCSV} iconName="Download">
          Exporteer naar CSV
        </Button>
      </div>

      {/* Period Selector */}
      <div className="bg-card border border-border rounded-lg p-4">
        <Select options={periodOptions} value={period} onChange={setPeriod} placeholder="Selecteer periode" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="FileText" size={24} className="text-primary" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground">{reportData?.totalQuotes}</h3>
          <p className="text-sm text-muted-foreground">Totaal Offertes</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground">{reportData?.wonQuotes}</h3>
          <p className="text-sm text-muted-foreground">Gewonnen Offertes</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Euro" size={24} className="text-success" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground">
            €{Math.round(reportData?.totalRevenue)?.toLocaleString('nl-NL')}
          </h3>
          <p className="text-sm text-muted-foreground">Totale Omzet</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={24} className="text-info" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground">
            {reportData?.conversionRate?.toFixed(1)}%
          </h3>
          <p className="text-sm text-muted-foreground">Conversie Percentage</p>
        </div>
      </div>

      {/* Top Project Types */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <span>Top Project Types</span>
        </h3>
        {reportData?.topProjectTypes?.length === 0 ? (
          <p className="text-muted-foreground text-sm">Geen projectgegevens beschikbaar</p>
        ) : (
          <div className="space-y-3">
            {reportData?.topProjectTypes?.map((project, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-foreground capitalize">{project?.type}</p>
                  <p className="text-xs text-muted-foreground">{project?.count} offertes</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-success">
                    €{Math.round(project?.revenue)?.toLocaleString('nl-NL')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activities */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-primary" />
          <span>Recente Activiteiten</span>
        </h3>
        {reportData?.recentActivities?.length === 0 ? (
          <p className="text-muted-foreground text-sm">Geen activiteiten beschikbaar</p>
        ) : (
          <div className="space-y-3">
            {reportData?.recentActivities?.map((activity) => (
              <div key={activity?.id} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Activity" size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">{activity?.activity_description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(activity?.activity_date)?.toLocaleString('nl-NL')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;